/**
 * UserInfo model for mobile client
 * Represents additional user information and preferences
 */
class Profile {
  constructor({
    id = null,
    userId = null,
    username = '',
    displayName = '',
    givenName = '',
    familyName = '',
    currentAvatar = null,
    currentBanner = null,
    bio = '',
    country = '',
    city = '',
    dateOfBirth = null,
    gender = '',
    guardianEmail = '',
    guardianRelationship = '',
    showNSFW = false,
    lookupValues = {},
    interests = [],
    companies = [],
    pronoun = '',
    createdAt = null,
    updatedAt = null,
  } = {}) {
    this.id = id;
    this.userId = userId;
    this.username = username;
    this.displayName = displayName;
    this.givenName = givenName;
    this.familyName = familyName;
    this.currentAvatar = currentAvatar;
    this.currentBanner = currentBanner;
    this.bio = bio;
    this.country = country;
    this.city = city;
    this.dateOfBirth = dateOfBirth ? new Date(dateOfBirth) : null;
    this.gender = gender;
    this.guardianEmail = guardianEmail;
    this.guardianRelationship = guardianRelationship;
    this.showNSFW = showNSFW;
    this.lookupValues = lookupValues;
    this.interests = interests;
    this.companies = companies;
    this.pronoun = pronoun;
    this.createdAt = createdAt ? new Date(createdAt) : new Date();
    this.updatedAt = updatedAt ? new Date(updatedAt) : new Date();
  }

  /**
   * Get full name by combining given and family names
   * @returns {string} Full name
   */
  get fullName() {
    return `${this.givenName} ${this.familyName}`.trim();
  }

  /**
   * Factory method to create a UserInfo instance from API response
   * @param {Object} data - Raw user info data from API
   * @returns {UserInfo} - Instantiated UserInfo object
   */
  static fromAPI(data) {
    if (!data) return null;
    
    return new Profile({
      id: data.id,
      userId: data.userId,
      username: data.username,
      displayName: data.displayName,
      givenName: data.givenName,
      familyName: data.familyName,
      currentAvatar: data.currentAvatar,
      currentBanner: data.currentBanner,
      bio: data.bio,
      country: data.country,
      city: data.city,
      dateOfBirth: data.dateOfBirth,
      gender: data.gender,
      guardianEmail: data.guardianEmail,
      guardianRelationship: data.guardianRelationship,
      showNSFW: data.showNSFW,
      lookupValues: data.lookupValues,
      interests: data.interests,
      companies: data.companies,
      pronoun: data.pronoun,
      createdAt: data.createdAt,
      updatedAt: data.updatedAt,
    });
  }

  /**
   * Convert UserInfo instance to plain object (for storage, etc.)
   * @returns {Object} Plain JavaScript object
   */
  toJSON() {
    return {
      id: this.id,
      userId: this.userId,
      username: this.username,
      displayName: this.displayName,
      givenName: this.givenName,
      familyName: this.familyName,
      currentAvatar: this.currentAvatar,
      currentBanner: this.currentBanner,
      bio: this.bio,
      country: this.country,
      city: this.city,
      dateOfBirth: this.dateOfBirth?.toISOString(),
      gender: this.gender,
      guardianEmail: this.guardianEmail,
      guardianRelationship: this.guardianRelationship,
      showNSFW: this.showNSFW,
      lookupValues: this.lookupValues,
      interests: this.interests,
      companies: this.companies,
      pronoun: this.pronoun,
      createdAt: this.createdAt.toISOString(),
      updatedAt: this.updatedAt.toISOString(),
    };
  }
}

export default Profile;
